import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { ImageUpload, UploadedImage } from '@/components/ui/ImageUpload'
import { Input } from '@/components/ui/Input'
import { getTodayDateString } from '@/lib/date'
import { useAuthStore } from '@/store/auth'
import { useRouteStore } from '@/store/route'
import { Report } from '@types'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import styles from './ReportForm.module.scss'

interface ReportFormData {
  description: string
}

interface ReportFormProps {
  existingReport: Report | undefined
  stopId: number
}

export const ReportForm = ({ existingReport, stopId }: ReportFormProps) => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const submitReport = useRouteStore((state) => state.submitReport)
  const currentVehicle = useRouteStore((state) =>
    state.vehicles.find((v) => v.id === state.selectedVehicleId)
  )

  const isEditing = !!existingReport

  const [images, setImages] = useState<UploadedImage[]>(() => {
    if (existingReport?.photos && existingReport.photos.length > 0) {
      return existingReport.photos.map((url, index) => ({
        id: `existing-${index}`,
        url,
        isUploading: false,
      }))
    }
    return []
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const methods = useForm<ReportFormData>({
    defaultValues: {
      description: existingReport?.description ?? '',
    },
  })

  const handleBack = () => {
    navigate(-1)
  }

  const onSubmit = async (data: ReportFormData) => {
    if (isSubmitting || !stopId) return

    setIsSubmitting(true)

    const imageUrls = images
      .filter((img) => !img.isUploading && !img.error)
      .map((img) => img.url)

    const result = await submitReport(stopId, {
      description: data.description,
      photos: imageUrls,
      reported_at: getTodayDateString(),
      user_name: user?.name,
      vehicle_name: currentVehicle?.name,
    })

    setIsSubmitting(false)

    if (result) {
      navigate(-1)
    }
  }

  return (
    <div className={styles.container} data-testid="report-form-page">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className={styles.form}
          data-testid="report-form"
        >
          <div className={styles.content}>
            {/* Header */}
            <header className={styles.header}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                data-testid="report-back-button"
              >
                <Icon
                  name="chevron-left"
                  size={20}
                  className={styles.backIcon}
                />
              </button>
              <h1 className={styles.title}>Report</h1>
            </header>

            {/* Description */}
            <div className={styles.descriptionField}>
              <Input<ReportFormData>
                name="description"
                mode="textarea"
                placeholder="What is wrong with the donation"
                rows={6}
                required
                testId="report-description"
              />
            </div>

            {/* Photos */}
            <ImageUpload
              images={images}
              onImagesChange={setImages}
              maxImages={6}
              testId="report-image-upload"
            />
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <Button
              text={isEditing ? 'Update Report' : 'File Report'}
              variant="warning"
              icon={<Icon name="arrow" size={16} />}
              type="submit"
              loading={isSubmitting}
              className={styles.submitButton}
              testId="report-submit-button"
            />
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
